import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as ExcelJS from 'exceljs';
import Papa from 'papaparse';
import { AllocationRow } from '../../types/tradeBooking';
import { TradingEntity } from '../../services/api/clientService';
import { GroupingsConfig } from '../../types/groupings';

interface AllocationExcelUploadProps {
  tradingEntities: TradingEntity[];
  groupingsConfig: GroupingsConfig | null;
  parentAmount: number | null;
  onRowsParsed: (rows: AllocationRow[]) => void;
  disabled: boolean;
}

/**
 * Upload template format (two-row header):
 *   Row 1: FX+ field names — fixed, never changes:
 *           entity | grouping1 | grouping2 | grouping3 | hedgeObject | notes | amount | pct
 *   Row 2: Client-friendly column headers — human-readable labels, ignored by the parser
 *   Row 3+: Data rows
 *
 * This approach eliminates all alias matching and locale conflicts.
 */
const VALID_FIELDS = ['entity', 'grouping1', 'grouping2', 'grouping3', 'hedgeObject', 'notes', 'amount', 'pct'] as const;

function buildColumnMap(fieldRow: string[]): Record<string, number> {
  const columnMap: Record<string, number> = {};
  fieldRow.forEach((cell, i) => {
    const field = cell.trim();
    if ((VALID_FIELDS as readonly string[]).includes(field) && !(field in columnMap)) {
      columnMap[field] = i;
    }
  });
  return columnMap;
}

function parseNumericValue(value: any): number | null {
  if (value == null || value === '') return null;
  // Strip explicit % suffix before numeric parsing (e.g. "10%" → "10")
  const rawStr = String(value).replace(/\s/g, '').replace(/%$/, '');
  // Handle common formats: 1,000.50 or 1.000,50
  let cleaned = rawStr;
  if (rawStr.includes(',') && rawStr.includes('.')) {
    // Determine which is the decimal separator (last one wins)
    const lastComma = rawStr.lastIndexOf(',');
    const lastDot = rawStr.lastIndexOf('.');
    if (lastComma > lastDot) {
      // European: 1.000,50
      cleaned = rawStr.replace(/\./g, '').replace(',', '.');
    } else {
      // US: 1,000.50
      cleaned = rawStr.replace(/,/g, '');
    }
  } else if (rawStr.includes(',')) {
    // Could be decimal comma (500,50) or thousand separator (1,000)
    const parts = rawStr.split(',');
    if (parts.length === 2 && parts[1].length <= 2) {
      cleaned = rawStr.replace(',', '.');
    } else {
      cleaned = rawStr.replace(/,/g, '');
    }
  }

  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

// Match a raw cell value against configured grouping values using prefix matching.
// "CO1001" matches "CO1001 - Azúcar Producción"; falls back to raw string if no match.
function resolveGroupingValue(raw: string, values: { id: string; name: string; isActive?: boolean }[]): string {
  if (!raw) return '';
  const lower = raw.toLowerCase();
  const exact = values.find(v => v.name.toLowerCase() === lower);
  if (exact) return exact.name;
  const prefix = values.find(v =>
    v.name.toLowerCase().startsWith(lower) || lower.startsWith(v.name.toLowerCase())
  );
  return prefix ? prefix.name : raw;
}

const AllocationExcelUpload: React.FC<AllocationExcelUploadProps> = ({
  tradingEntities,
  groupingsConfig,
  parentAmount,
  onRowsParsed,
  disabled,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const processRows = (
    rawRows: Record<string, any>[],
    columnMap: Record<string, number>,
    startRowNum: number,
  ): { rows: AllocationRow[]; errors: string[] } => {
    const errs: string[] = [];
    const rows: AllocationRow[] = [];

    // Entity column is required
    if (!('entity' in columnMap)) {
      return { rows: [], errors: [t('tradeBooking.allocation.columnNotFound', { column: 'entity' })] };
    }

    // At least amount or pct is required
    if (!('amount' in columnMap) && !('pct' in columnMap)) {
      return { rows: [], errors: [t('tradeBooking.allocation.columnNotFound', { column: 'amount / pct' })] };
    }

    for (let i = 0; i < rawRows.length; i++) {
      const raw = rawRows[i];
      // Build a sparse array keyed by actual column index (gaps for empty cells must be preserved)
      const values: any[] = [];
      Object.entries(raw).forEach(([k, v]) => { values[Number(k)] = v; });
      const rowNum = startRowNum + i;

      // Get entity name and look up
      const entityName = String(values[columnMap.entity] ?? '').trim();
      if (!entityName) {
        errs.push(`${t('tradeBooking.allocation.row')} ${rowNum}: ${t('tradeBooking.allocation.entityNotFound', { name: '(empty)' })}`);
        continue;
      }

      const entity = tradingEntities.find(
        e => e.name.toLowerCase() === entityName.toLowerCase()
      );
      if (!entity) {
        errs.push(`${t('tradeBooking.allocation.row')} ${rowNum}: ${t('tradeBooking.allocation.entityNotFound', { name: entityName })}`);
        continue;
      }

      // Parse groupings
      const g1Raw = columnMap.grouping1 != null ? String(values[columnMap.grouping1] ?? '').trim() : '';
      const g2Raw = columnMap.grouping2 != null ? String(values[columnMap.grouping2] ?? '').trim() : '';
      const g3Raw = columnMap.grouping3 != null ? String(values[columnMap.grouping3] ?? '').trim() : '';
      const g1 = resolveGroupingValue(g1Raw, groupingsConfig?.grouping1Values ?? []);
      const g2 = resolveGroupingValue(g2Raw, groupingsConfig?.grouping2Values ?? []);
      const g3 = resolveGroupingValue(g3Raw, groupingsConfig?.grouping3Values ?? []);
      const hedgeObject = columnMap.hedgeObject != null ? String(values[columnMap.hedgeObject] ?? '').trim() : '';
      const notes = columnMap.notes != null ? String(values[columnMap.notes] ?? '').trim() : '';

      // Parse amount and percentage
      const rawAmount = columnMap.amount != null ? parseNumericValue(values[columnMap.amount]) : null;
      const rawPct = columnMap.pct != null ? parseNumericValue(values[columnMap.pct]) : null;

      let amount: number | null = rawAmount;
      let percentage: number | null = rawPct;

      // Calculate missing value from the other
      if (amount == null && percentage != null && parentAmount && parentAmount > 0) {
        amount = Math.round((percentage / 100) * parentAmount * 100) / 100;
      } else if (percentage == null && amount != null && parentAmount && parentAmount > 0) {
        percentage = Math.round((amount / parentAmount) * 10000) / 100;
      }

      if (amount == null && percentage == null) {
        errs.push(`${t('tradeBooking.allocation.row')} ${rowNum}: ${t('tradeBooking.allocation.invalidAmount')}`);
        continue;
      }

      rows.push({
        entityId: entity.id,
        entityName: entity.name,
        entityTaxId: entity.taxId || '',
        grouping1: g1,
        grouping2: g2,
        grouping3: g3,
        amount,
        percentage,
        hedgeObject,
        notes,
      });
    }

    return { rows, errors: errs };
  };

  const handleExcelFile = async (file: File) => {
    try {
      const buffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);

      const worksheet = workbook.worksheets[0];
      if (!worksheet || worksheet.rowCount < 3) {
        setErrors([t('tradeBooking.allocation.importErrors') + ': File must have at least 3 rows (field names, column headers, data)']);
        return;
      }

      // Row 1: FX+ field names (exact match, no alias resolution)
      const fieldRow = worksheet.getRow(1);
      const fieldNames: string[] = [];
      fieldRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        fieldNames[colNumber - 1] = String(cell.value ?? '');
      });
      const columnMap = buildColumnMap(fieldNames);

      // Row 2: client-friendly display headers — skip
      // Data starts at row 3
      const rawRows: Record<string, any>[] = [];
      for (let rowIndex = 3; rowIndex <= worksheet.rowCount; rowIndex++) {
        const row = worksheet.getRow(rowIndex);
        const rowData: Record<string, any> = {};
        let hasData = false;
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          let val = cell.value;
          // If Excel has formatted this cell as a percentage, the value is stored
          // as a decimal (e.g. 10% → 0.1). Convert back to a readable percentage.
          if (typeof val === 'number' && (cell.numFmt || '').includes('%')) {
            val = Math.round(val * 100 * 1e6) / 1e6;
          }
          // Security: only accept string/number types
          if (typeof val === 'string' || typeof val === 'number') {
            rowData[colNumber - 1] = val;
            if (val !== '' && val !== 0) hasData = true;
          } else if (val != null) {
            rowData[colNumber - 1] = String(val);
            hasData = true;
          }
        });
        if (hasData) rawRows.push(rowData);
      }

      const result = processRows(rawRows, columnMap, 3);
      if (result.errors.length > 0) {
        setErrors(result.errors);
      } else {
        setErrors([]);
        onRowsParsed(result.rows);
      }
    } catch (err) {
      setErrors([t('tradeBooking.allocation.importErrors') + ': ' + String(err)]);
    }
  };

  const handleCsvFile = (file: File) => {
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        if (!results.data || results.data.length < 3) {
          setErrors([t('tradeBooking.allocation.importErrors') + ': File must have at least 3 rows (field names, column headers, data)']);
          return;
        }

        const allRows = results.data as string[][];
        // Row 0: FX+ field names
        const fieldNames = allRows[0].map(h => String(h));
        // Row 1: display headers — skip
        // Data starts at row index 2
        const dataRows = allRows.slice(2);
        const columnMap = buildColumnMap(fieldNames);

        // Convert array rows to indexed objects
        const rawRows = dataRows.map(row => {
          const obj: Record<string, any> = {};
          row.forEach((val, i) => { obj[i] = val; });
          return obj;
        });

        const result = processRows(rawRows, columnMap, 3);
        if (result.errors.length > 0) {
          setErrors(result.errors);
        } else {
          setErrors([]);
          onRowsParsed(result.rows);
        }
      },
      error: (err) => {
        setErrors([t('tradeBooking.allocation.importErrors') + ': ' + err.message]);
      },
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'csv') {
      handleCsvFile(file);
    } else if (ext === 'xlsx' || ext === 'xls') {
      handleExcelFile(file);
    } else {
      setErrors([t('tradeBooking.allocation.importErrors') + ': Unsupported file type']);
    }

    // Reset input so same file can be re-uploaded
    event.target.value = '';
  };

  return (
    <div className="allocation-upload">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <button
        type="button"
        className="btn btn-outline btn-sm allocation-upload-btn"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
      >
        {t('tradeBooking.allocation.uploadExcel')}
      </button>

      {errors.length > 0 && (
        <div className="allocation-upload-errors">
          {errors.map((err, i) => (
            <div key={i} className="upload-error-line">{err}</div>
          ))}
          <button
            type="button"
            className="upload-errors-dismiss"
            onClick={() => setErrors([])}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default AllocationExcelUpload;
